import type { OnboardingIntent } from "./onboardingV2";

import * as z from "zod";

/** What a project sells (`project.vertical`): it picks the set of workflows, and integrations then pick the version within it. */
export const projectVerticalSchema = z
  .enum(["ecommerce", "saas", "services"])
  .describe("ProjectVertical");

export type ProjectVertical = z.infer<typeof projectVerticalSchema>;

/** Every project is ecommerce until something says otherwise. */
export const DEFAULT_PROJECT_VERTICAL: ProjectVertical = "ecommerce";

/** Who last set a project's vertical (`project.vertical_source`); detection never writes over `user`. */
export const projectVerticalSourceSchema = z
  .enum(["unset", "detected", "user"])
  .describe("ProjectVerticalSource");

export type ProjectVerticalSource = z.infer<typeof projectVerticalSourceSchema>;

/** The sets of workflows written for one vertical. A workflow in neither is shared by every vertical. */
export const workflowVerticalSchema = z
  .enum(["ecommerce", "saas", "services"])
  .describe("WorkflowVertical");

export type WorkflowVertical = z.infer<typeof workflowVerticalSchema>;

/** The set of workflows each vertical is offered. */
export const VERTICAL_WORKFLOW_SETS: Record<ProjectVertical, WorkflowVertical> =
  {
    ecommerce: "ecommerce",
    saas: "saas",
    services: "services",
  };

/** What onboarding knows about a project when it detects the vertical. Null is a fact that is not known. */
export type VerticalFacts = {
  /** Detection found a Shopify store at the project's website. */
  isShopifyStore: boolean;
  /** "What are you trying to do?" as answered in onboarding. */
  intent: OnboardingIntent | null;
  /** The workflow set of the project's category (`PROJECT_CATEGORIES[category].vertical`). */
  categoryVertical?: ProjectVertical | null;
  /** How the business makes money, as the brand import lists it: "dtc, subscription", "b2b, saas". */
  businessModel: string | null;
};

const INTENT_VERTICALS: Record<OnboardingIntent, ProjectVertical> = {
  physical: "ecommerce",
  merch: "ecommerce",
  other: "ecommerce",
  software: "saas",
  services: "services",
};

/** Business-model labels that mean the business sells goods. */
const GOODS_MODELS =
  /\b(dtc|d2c|direct[- ]to[- ]consumer|retail(er)?|e-?commerce|wholesale|cpg|consumer goods)\b/;

/** Labels that mean it sells software. `b2b`, `subscription` and `marketplace` fit more than one vertical and decide nothing. */
const SOFTWARE_MODELS =
  /\b(saas|software|platform|freemium|usage[- ]based|licens(e|ing)|fintech|(financial|web|cloud) services)\b/;

/** Labels that mean it sells a service. A bare "services" beside `b2b` or `subscription` describes software as often as a studio, so it decides nothing and the person is asked. */
const SERVICES_MODELS =
  /\b(agency|consult(ing|ancy)|(professional|local|home) services?|appointments?|bookings?|class packs?|memberships?)\b/;
/** Trades only a service business names. A word a store or a software company also uses ("cleaning", "studio", "fitness") is left out. */
const SERVICES_TRADES =
  /\b(real estate|realty|brokerage|(house|home|office|commercial) cleaning|cleaning services?|salons?|barbershops?|(day|med) spas?|clinics?|dental (practice|care)|(pilates|yoga|fitness|dance) studios?|plumbing|hvac|landscaping|law firms?)\b/;
const BARE_SERVICES = /\bservices?\b/;
const AMBIGUOUS_WITH_SERVICES = /\b(b2b|subscription)\b/;

/** The vertical onboarding is confident of, else null; first match wins: Shopify store, the person's intent answer, the category, business model (goods beat software). */
export function detectProjectVertical(
  facts: VerticalFacts,
): ProjectVertical | null {
  if (facts.isShopifyStore) {
    return "ecommerce";
  }
  if (facts.intent) {
    return INTENT_VERTICALS[facts.intent];
  }
  if (facts.categoryVertical) {
    return facts.categoryVertical;
  }
  const model = (facts.businessModel ?? "").toLowerCase();
  if (GOODS_MODELS.test(model)) {
    return "ecommerce";
  }
  if (SOFTWARE_MODELS.test(model)) {
    return "saas";
  }
  if (
    SERVICES_MODELS.test(model) ||
    SERVICES_TRADES.test(model) ||
    (BARE_SERVICES.test(model) && !AMBIGUOUS_WITH_SERVICES.test(model))
  ) {
    return "services";
  }
  return null;
}
